import React, { useMemo, useState } from 'react';

function getZodiacSign(month, day) {
  const ranges = [
    { sign: 'Oğlak', from: [12, 22], to: [1, 19] },
    { sign: 'Kova', from: [1, 20], to: [2, 18] },
    { sign: 'Balık', from: [2, 19], to: [3, 20] },
    { sign: 'Koç', from: [3, 21], to: [4, 19] },
    { sign: 'Boğa', from: [4, 20], to: [5, 20] },
    { sign: 'İkizler', from: [5, 21], to: [6, 20] },
    { sign: 'Yengeç', from: [6, 21], to: [7, 22] },
    { sign: 'Aslan', from: [7, 23], to: [8, 22] },
    { sign: 'Başak', from: [8, 23], to: [9, 22] },
    { sign: 'Terazi', from: [9, 23], to: [10, 22] },
    { sign: 'Akrep', from: [10, 23], to: [11, 21] },
    { sign: 'Yay', from: [11, 22], to: [12, 21] }
  ];
  for (const { sign, from, to } of ranges) {
    const [fromM, fromD] = from;
    const [toM, toD] = to;
    if (
      (month === fromM && day >= fromD) ||
      (month === toM && day <= toD) ||
      (fromM < toM && month > fromM && month < toM) ||
      (fromM > toM && (month > fromM || month < toM))
    ) {
      return sign;
    }
  }
  return 'Bilinmiyor';
}

const ZODIAC_ORDER = ['Koç','Boğa','İkizler','Yengeç','Aslan','Başak','Terazi','Akrep','Yay','Oğlak','Kova','Balık']; // 0..11
const HOUSE_LABELS = ['I','II','III','IV','V','VI','VII','VIII','IX','X','XI','XII'];

function signToIndex(signName) {
  const idx = ZODIAC_ORDER.indexOf(signName);
  return idx >= 0 ? idx : 0;
}

function approxAscIndexFromTime(timeStr) {
  if (!timeStr) return 0; // Koç varsayalım
  const [hh, mm] = timeStr.split(':').map((x) => parseInt(x || '0', 10));
  const hours = Number.isFinite(hh) ? hh : 0;
  const minutes = Number.isFinite(mm) ? mm : 0;
  const idx = Math.floor(((hours + minutes / 60) / 2)) % 12; // 2 saat = 1 burç
  return (idx + 12) % 12;
}

function createSeededRng(seed) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
  }
  h >>>= 0;
  return function next() {
    // xorshift32
    h ^= h << 13; h >>>= 0;
    h ^= h >> 17; h >>>= 0;
    h ^= h << 5;  h >>>= 0;
    return (h % 10000) / 10000; // [0,1)
  };
}

function degreesForSignIndex(idx) {
  return (idx * 30) % 360;
}

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = (angleDeg - 90) * Math.PI / 180; // 0° yukarı baksın
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function AstroChart({ birthDate, birthTime, sunSign, seedParts }) {
  const size = 420;
  const cx = size / 2;
  const cy = size / 2;
  const outerR = 180;
  const innerR = 120;
  const coreR = 70;

  const ascIndex = useMemo(() => approxAscIndexFromTime(birthTime), [birthTime]);
  const ascDeg = degreesForSignIndex(ascIndex);

  const rng = useMemo(() => createSeededRng(`${birthDate}|${birthTime}|${sunSign}|${seedParts || ''}`), [birthDate, birthTime, sunSign, seedParts]);

  const planets = useMemo(() => {
    const sunIdx = signToIndex(sunSign);
    const sunAngle = (degreesForSignIndex(sunIdx) + 15) % 360; // burcun ortası
    const names = ['Güneş','Ay','Merkür','Venüs','Mars','Jüpiter','Satürn'];
    return names.map((name, i) => {
      let angle = i === 0 ? sunAngle : Math.floor(rng() * 360);
      // merkezden farklı halkaya koy
      const radius = coreR - 8 + (i % 3) * 7;
      return { name, angle, radius };
    });
  }, [rng, sunSign]);

  // SVG parçaları
  const houseLines = new Array(12).fill(0).map((_, i) => {
    const angle = (ascDeg + i * 30) % 360; // eşit evler, 1.ev = ASC
    const pOuter = polarToCartesian(cx, cy, outerR, angle);
    const pInner = polarToCartesian(cx, cy, coreR, angle);
    return { angle, pOuter, pInner };
  });

  const ring = (
    <g>
      <circle cx={cx} cy={cy} r={outerR} fill="#0f1430" stroke="#27346a" strokeWidth="2" />
      <circle cx={cx} cy={cy} r={innerR} fill="#111731" stroke="#27346a" strokeWidth="2" />
      <circle cx={cx} cy={cy} r={coreR} fill="#0b1020" stroke="#27346a" strokeWidth="1.5" />
    </g>
  );

  const houseGrid = (
    <g>
      {houseLines.map((ln, idx) => (
        <line
          key={idx}
          x1={ln.pInner.x}
          y1={ln.pInner.y}
          x2={ln.pOuter.x}
          y2={ln.pOuter.y}
          stroke="#2b3b79"
          strokeOpacity="0.8"
          strokeWidth={idx % 3 === 0 ? 1.6 : 1}
        />
      ))}
      {/* House labels */}
      {new Array(12).fill(0).map((_, i) => {
        const midAngle = (ascDeg + i * 30 + 15) % 360;
        const p = polarToCartesian(cx, cy, (innerR + outerR) / 2, midAngle);
        return (
          <text key={`h${i}`} x={p.x} y={p.y} fill="#9fb3ff" fontSize="12" textAnchor="middle" dominantBaseline="middle">
            {HOUSE_LABELS[i]}
          </text>
        );
      })}
    </g>
  );

  const zodiacBelt = (
    <g>
      {ZODIAC_ORDER.map((zn, i) => {
        const ang = (i * 30) % 360;
        const mid = (ang + 15) % 360;
        const labelPos = polarToCartesian(cx, cy, innerR - 14, mid);
        const isAscSign = i === ascIndex;
        return (
          <g key={zn}>
            <path
              d={describeArc(cx, cy, innerR - 4, ang, ang + 30)}
              fill="none"
              stroke={isAscSign ? '#5b7cff' : '#27346a'}
              strokeWidth={isAscSign ? 2.2 : 1}
              strokeOpacity={isAscSign ? 1 : 0.8}
            />
            <text x={labelPos.x} y={labelPos.y} fill={isAscSign ? '#ffffff' : '#c7d2ff'} fontSize="10" textAnchor="middle" dominantBaseline="middle">
              {zn}
            </text>
          </g>
        );
      })}
    </g>
  );

  const planetDots = (
    <g>
      {planets.map((pl) => {
        const pos = polarToCartesian(cx, cy, pl.radius, pl.angle);
        return (
          <g key={pl.name}>
            <circle cx={pos.x} cy={pos.y} r="4" fill="#5b7cff" stroke="#c7d2ff" strokeWidth="1" />
          </g>
        );
      })}
    </g>
  );

  const planetLegend = (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, marginTop: 12 }}>
      {planets.map((pl) => (
        <div key={pl.name} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, opacity: .9 }}>
          <span style={{ width: 10, height: 10, background: '#5b7cff', border: '1px solid #c7d2ff', borderRadius: 6, display: 'inline-block' }} />
          <span style={{ color: '#e6e8ef' }}>{pl.name}</span>
          <span style={{ color: '#9fb3ff' }}>{Math.round(pl.angle)}°</span>
        </div>
      ))}
    </div>
  );

  return (
    <div>
      <svg width="100%" height={size} viewBox={`0 0 ${size} ${size}`} style={{ background: 'transparent' }}>
        {ring}
        {houseGrid}
        {zodiacBelt}
        {planetDots}
        {/* ASC oku */}
        <g>
          {(() => {
            const p = polarToCartesian(cx, cy, outerR + 8, ascDeg);
            const q = polarToCartesian(cx, cy, outerR - 2, ascDeg);
            return <line x1={q.x} y1={q.y} x2={p.x} y2={p.y} stroke="#5b7cff" strokeWidth="2.5" />;
          })()}
        </g>
      </svg>
      {planetLegend}
      <p style={{ fontSize: 11, opacity: .65, marginTop: 8 }}>
        Bu harita eşit evler yaklaşımıyla ve basit yaklaşımla oluşturulmuş bir prototiptir (gerçek efemeris kullanılmaz).
      </p>
    </div>
  );
}

// SVG yardımcı: daire yayı
function describeArc(x, y, radius, startAngle, endAngle) {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
  const d = ['M', start.x, start.y, 'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y].join(' ');
  return d;
}

const fieldClass =
  "bg-[#0f1430] border border-[#27346a] text-[#e6e8ef] px-3 py-2 rounded-lg outline-none focus:border-[#5b7cff] focus:ring-2 focus:ring-[#5b7cff]/20";

export default function KediKopekBurc() {
  const [form, setForm] = useState({
    ownerName: '',
    petType: 'kedi',
    petName: '',
    breed: '',
    birthDate: '',
    birthTime: '',
    birthPlace: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const sunSign = useMemo(() => {
    if (!form.birthDate) return '';
    const dt = new Date(form.birthDate);
    if (Number.isNaN(dt.getTime())) return '';
    return getZodiacSign(dt.getMonth() + 1, dt.getDate());
  }, [form.birthDate]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitted(false);
    setError('');
    if (!form.birthDate) {
      setError('Doğum tarihi zorunludur.');
      return;
    }
    setSubmitted(true);
  };

  return (
    <div style={{ background: '#0b1020', minHeight: '100vh' }}>
      <div className="container" style={{ maxWidth: 760, margin: '0 auto', padding: 24, color: '#e6e8ef' }}>
        <div className="card" style={{ background: '#111731', border: '1px solid #1e2a55', borderRadius: 12, padding: 24, boxShadow: '0 10px 30px rgba(0,0,0,0.35)' }}>
          <h1 style={{ fontSize: 20, margin: '0 0 12px' }}>Kedi/Köpek Doğum Haritası (Gizli)</h1>
          <p style={{ opacity: .8, fontSize: 14, margin: '0 0 20px' }}>
            Bu sayfa gizli bir denemedir. URL: <span style={{ display: 'inline-block', padding: '4px 8px', borderRadius: 999, background: '#1a244a', border: '1px solid #2b3b79', fontSize: 12 }}>/kedikopekburc</span>
          </p>
          {error ? (
            <div style={{ background: '#3a1320', border: '1px solid #7a2742', color: '#ffd1db', padding: '10px 12px', borderRadius: 8, marginBottom: 12 }}>
              {error}
            </div>
          ) : null}
          <form onSubmit={onSubmit} noValidate>
            <div className="row" style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr' }}>
              <div className="field" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 12, opacity: .9 }} htmlFor="ownerName">Sahip Adı</label>
                <input id="ownerName" name="ownerName" placeholder="Ad Soyad" className={fieldClass} value={form.ownerName} onChange={onChange} />
              </div>
              <div className="field" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 12, opacity: .9 }} htmlFor="petType">Evcil Türü</label>
                <select id="petType" name="petType" className={fieldClass} value={form.petType} onChange={onChange}>
                  <option value="kedi">Kedi</option>
                  <option value="köpek">Köpek</option>
                </select>
              </div>
            </div>
            <div className="row" style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr', marginTop: 12 }}>
              <div className="field" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 12, opacity: .9 }} htmlFor="petName">Evcil Adı</label>
                <input id="petName" name="petName" placeholder="Mırmır / Karabaş" className={fieldClass} value={form.petName} onChange={onChange} />
              </div>
              <div className="field" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 12, opacity: .9 }} htmlFor="breed">Cins (opsiyonel)</label>
                <input id="breed" name="breed" placeholder="British Shorthair / Golden" className={fieldClass} value={form.breed} onChange={onChange} />
              </div>
            </div>
            <div className="row-3" style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr 1fr', marginTop: 12 }}>
              <div className="field" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 12, opacity: .9 }} htmlFor="birthDate">Doğum Tarihi</label>
                <input id="birthDate" name="birthDate" type="date" className={fieldClass} value={form.birthDate} onChange={onChange} required />
              </div>
              <div className="field" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 12, opacity: .9 }} htmlFor="birthTime">Doğum Saati (opsiyonel)</label>
                <input id="birthTime" name="birthTime" type="time" className={fieldClass} value={form.birthTime} onChange={onChange} />
                <span style={{ fontSize: 12, opacity: .7 }}>Bilmesen de olur</span>
              </div>
              <div className="field" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 12, opacity: .9 }} htmlFor="birthPlace">Doğum Yeri (opsiyonel)</label>
                <input id="birthPlace" name="birthPlace" placeholder="İl/İlçe" className={fieldClass} value={form.birthPlace} onChange={onChange} />
              </div>
            </div>
            <div className="actions" style={{ marginTop: 16, display: 'flex', gap: 12 }}>
              <button type="submit" style={{ background: '#5b7cff', color: '#fff', border: 0, padding: '10px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
                Haritayı Oluştur
              </button>
              <button type="button" onClick={() => { setForm({ ownerName: '', petType: 'kedi', petName: '', breed: '', birthDate: '', birthTime: '', birthPlace: '' }); setSubmitted(false); setError(''); }} style={{ background: 'transparent', border: '1px solid #32407a', color: '#e6e8ef', padding: '10px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
                Temizle
              </button>
            </div>
          </form>
          {submitted ? (
            <div className="result" style={{ marginTop: 16, borderTop: '1px dashed #27346a', paddingTop: 16 }}>
              <h3>Doğum Haritası Özeti</h3>
              <div className="grid" style={{ display: 'grid', gap: 10, gridTemplateColumns: '1fr 1fr' }}>
                <div><strong>Evcil Adı:</strong> {form.petName || '-'}</div>
                <div><strong>Tür:</strong> {form.petType}</div>
                <div><strong>Cins:</strong> {form.breed || '-'}</div>
                <div><strong>Doğum:</strong> {form.birthDate}{form.birthTime ? ` ${form.birthTime}` : ''}{form.birthPlace ? ` – ${form.birthPlace}` : ''}</div>
                <div><strong>Güneş Burcu:</strong> <span style={{ display: 'inline-block', padding: '4px 8px', borderRadius: 999, background: '#1a244a', border: '1px solid #2b3b79', fontSize: 12 }}>{sunSign || '-'}</span></div>
              </div>
              <div style={{ marginTop: 16 }}>
                <h4 style={{ margin: '0 0 8px', fontSize: 16 }}>12 Ev Doğum Haritası (Prototip)</h4>
                <AstroChart
                  birthDate={form.birthDate}
                  birthTime={form.birthTime}
                  sunSign={sunSign}
                  seedParts={`${form.petName}|${form.petType}|${form.breed}`}
                />
              </div>
              <p style={{ fontSize: 12, opacity: .7, marginTop: 8 }}>Not: Bu bir prototip. İleride tam harita çizimi ve yorumlar eklenecek.</p>
            </div>
          ) : null}
          <div className="footer" style={{ marginTop: 10, fontSize: 11, opacity: .6 }}>Glowytics Labs – Sadece dahili deneme</div>
        </div>
      </div>
    </div>
  );
}


